import {
  TaskComment,
  TaskCommentFieldAnchor,
  TaskCommentFieldAnchorState,
} from '../../types';

/**
 * Pure helpers for the two sides of the review loop's progress gates. Kept
 * outside React so they can be unit-tested with a plain fixture list and
 * reused by the footer + banner without duplicating the logic.
 *
 * Both helpers expect the ALREADY-FILTERED `fieldComments` list the shell
 * passes to the comment column — i.e. pending LLM comments are already
 * stripped for non-reviewer viewers. As defense in depth they also skip
 * `source === 'llm' && llmStatus !== 'approved'` themselves, so accidental
 * callers can't accidentally block the writer on AI feedback the writer
 * can't see.
 */

function isVisibleRoot(c: TaskComment): boolean {
  if (c.parentCommentId != null) return false;
  if (c.source === 'llm' && c.llmStatus !== 'approved') return false;
  return true;
}

/**
 * Reviewer-side gate: the "Request changes" button should only be enabled
 * when the reviewer has actually left unresolved feedback. Without that, the
 * writer would get the task bounced back with no context to revise against.
 *
 * Scope: any unresolved root on the task (NOT scoped to the current round),
 * since comments from earlier rounds that were never resolved are still
 * legitimate grounds for a changes-requested bounce.
 */
export function canRequestChanges(comments: TaskComment[]): boolean {
  return comments.some((c) => isVisibleRoot(c) && !c.addressed);
}

export interface ResubmitBlocker {
  commentId: string;
  fieldPath: string | null;
  fieldAnchor?: TaskCommentFieldAnchor;
  fieldAnchorState?: TaskCommentFieldAnchorState;
}

export interface ResubmitGate {
  ok: boolean;
  /** Roots from the latest round the writer hasn't yet resolved or replied to. */
  blockedBy: ResubmitBlocker[];
}

/**
 * Writer-side gate: the writer must engage with every unresolved reviewer
 * comment on the task — either resolve it (it's been fixed) or reply to it
 * (acknowledge / push back) — before they can resubmit. A silent re-submit
 * without engaging is the behavior this gate is explicitly designed to
 * prevent.
 *
 * We do NOT scope by `reviewRound`: an unresolved comment from an earlier
 * review cycle is still unresolved, and the writer's mental model is
 * "unresolved feedback exists → I can't resubmit yet." Scoping to the
 * current round only would silently amnesty older ghost threads.
 *
 * A reviewer reply on a root doesn't unblock the writer — only the writer's
 * own reply counts toward the "responded" condition.
 */
export function canResubmit(
  comments: TaskComment[],
  ownerId: string | null,
): ResubmitGate {
  const repliesByRoot = new Map<string, TaskComment[]>();
  for (const c of comments) {
    if (c.parentCommentId == null) continue;
    const list = repliesByRoot.get(c.parentCommentId);
    if (list) list.push(c);
    else repliesByRoot.set(c.parentCommentId, [c]);
  }

  const blockedBy: ResubmitBlocker[] = [];
  for (const c of comments) {
    if (!isVisibleRoot(c)) continue;
    if (c.addressed) continue;
    const replies = repliesByRoot.get(c.id) ?? [];
    const hasWriterReply = ownerId
      ? replies.some((r) => r.authorId === ownerId)
      : false;
    if (hasWriterReply) continue;
    const blocker: ResubmitBlocker = {
      commentId: c.id,
      fieldPath: c.fieldPath,
    };
    if (c.fieldAnchor) blocker.fieldAnchor = c.fieldAnchor;
    if (c.fieldAnchorState) blocker.fieldAnchorState = c.fieldAnchorState;
    blockedBy.push(blocker);
  }

  return { ok: blockedBy.length === 0, blockedBy };
}
